import { queryClient } from "../../Utils/tanstack-query";

export const updateQueryCacheWithNewSessionKeys = (
  key: (string | any | undefined)[],
  newSession: Partial<any>
) => {
  if (!newSession?.session_id) return;

  //🟢 Optimistically update React Query cache with the updated session keys
  queryClient.setQueryData(key, (oldData: any) => {
    if (!oldData) return oldData;

    //🟢 Find the session to be updated
    let sessionToUpdate: any = null;
    const updatedPages = oldData?.pages?.map((page: any, index: number) => {
      const filteredSessions = page?.data?.data?.filter((session: any) => {
        if (session.session_id === newSession?.session_id) {
          sessionToUpdate = session;
          return false;
        }
        return true;
      });

      // Prepend updated session to the first page
      if (index === 0 && sessionToUpdate) {
        const updatedSession = { ...sessionToUpdate, ...newSession };
        return {
          ...page,
          data: {
            ...page.data,
            data: [updatedSession, ...filteredSessions],
          },
        };
      }

      return { ...page, data: { ...page.data, data: filteredSessions } };
    });

    // If the session was not found, add it to the top of the first page
    if (!sessionToUpdate && updatedPages?.length > 0) {
      const firstPage = updatedPages[0];
      const newSessionData = {
        ...firstPage,
        data: {
          ...firstPage.data,
          data: [newSession, ...firstPage.data.data],
        },
      };

      updatedPages[0] = newSessionData;
    }

    return { ...oldData, pages: updatedPages };
  });
};

export const updateQueryCacheInPlace = (
  key: (string | any | undefined)[],
  newSession: Partial<any>
) => {
  if (!newSession?.session_id) return;

  //🟢 Update React Query cache without changing position
  queryClient.setQueryData(key, (oldData: any) => {
    if (!oldData) return oldData;

    //🟢 Find and update the session in its current position
    const updatedPages = oldData?.pages?.map((page: any) => {
      const updatedSessions = page?.data?.data?.map((session: any) => {
        if (session.session_id === newSession?.session_id) {
          // Update the session while keeping it in the same position
          return { ...session, ...newSession };
        }
        return session;
      });

      return { 
        ...page, 
        data: { 
          ...page.data, 
          data: updatedSessions 
        } 
      };
    });

    return { ...oldData, pages: updatedPages };
  });
};


export const updateUserInfoDataCache = (queryKey: any, newSession: any) => {
  if (!newSession?.session_id) return;

  const cachedHistory: any = queryClient.getQueryData(queryKey);

  if (cachedHistory) {
    const updatedHistory = {
      ...cachedHistory,
      ...newSession
    };
    queryClient.setQueryData(queryKey, updatedHistory, {
      updatedAt: Date.now(),
    });
  }
};


export const addNewMessageToCache = (newMessage: any): void => {
  const sessionId = newMessage?.[newMessage?.length - 1]?.session_id;
  if (!sessionId || !newMessage) {
    return;
  }

  const queryKey = ["conversations", "chat-history", sessionId, "messages"];

  // Get current cached data - if no cache exists, do nothing
  const currentData: any = queryClient.getQueryData(queryKey);
  if (!currentData) {
    console.log("No cache found for session:", sessionId);
    return;
  }

  // Update the cache by adding the single message to the last page
  queryClient.setQueryData(queryKey, (oldData: any) => {
    if (!oldData || !oldData.pages) return oldData;

    const newData = { ...oldData };
    newData.pages = [...oldData.pages];
    const firstPage = newData.pages[0] || [];
    newData.pages[0] = [...newMessage, ...firstPage];

    return newData;
  });
};
